// NinioCardComponents.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    Alert,
    AppBar,
    Toolbar,
    Stepper,
    Step,
    StepLabel,
    LinearProgress,
    Avatar,
    Box,
    Button,
    Card,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    IconButton,
    MenuItem,
    Stack,
    TextField,
    Tooltip,
    Typography,
    FormControlLabel,
    Checkbox,
} from "@mui/material";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import UndoIcon from "@mui/icons-material/Undo";
import CloseIcon from "@mui/icons-material/Close";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import Cropper from "react-easy-crop";
import imageCompression from "browser-image-compression";
import Fuse from "fuse.js";
import {
    supabase,
    todayDateOnly,
    parseYMD,
    readAll,
    withPhotos,
} from "./cjeApi";

export const PhotoCropDialog = ({ open, onClose, rawFile, onConfirm }) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!rawFile || !open) return;
        const url = URL.createObjectURL(rawFile);
        setImageSrc(url);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
        setError("");
        return () => URL.revokeObjectURL(url);
    }, [rawFile, open]);

    const handleConfirm = async () => {
        if (!croppedAreaPixels || busy) return;
        setBusy(true);
        setError("");

        try {
            const image = new Image();
            await new Promise((resolve, reject) => {
                image.onload = resolve;
                image.onerror = () =>
                    reject(new Error("No se pudo leer la imagen"));
                image.src = imageSrc;
            });

            const canvas = document.createElement("canvas");
            canvas.width = canvas.height = Math.min(
                600,
                croppedAreaPixels.width,
            );
            canvas
                .getContext("2d")
                .drawImage(
                    image,
                    croppedAreaPixels.x,
                    croppedAreaPixels.y,
                    croppedAreaPixels.width,
                    croppedAreaPixels.height,
                    0,
                    0,
                    canvas.width,
                    canvas.height,
                );

            const blob = await new Promise((resolve, reject) =>
                canvas.toBlob(
                    (b) =>
                        b
                            ? resolve(b)
                            : reject(
                                  new Error("No se pudo recortar la imagen"),
                              ),
                    "image/jpeg",
                    0.85,
                ),
            );

            const compressed = await imageCompression(
                new File([blob], "foto.jpg", { type: "image/jpeg" }),
                { maxSizeMB: 0.2, maxWidthOrHeight: 600, useWebWorker: true },
            );

            await onConfirm(
                new File([compressed], "foto.jpg", { type: "image/jpeg" }),
            );
        } catch (e) {
            setError(e.message || "No se pudo procesar la foto");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog fullScreen open={open} onClose={busy ? undefined : onClose}>
            <AppBar sx={{ position: "relative" }}>
                <Toolbar>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={onClose}
                        disabled={busy}
                    >
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6">
                        Recorta tu foto
                    </Typography>
                    <Button
                        color="inherit"
                        onClick={handleConfirm}
                        disabled={busy || !croppedAreaPixels}
                    >
                        {busy ? "Procesando..." : "Usar"}
                    </Button>
                </Toolbar>
            </AppBar>
            {error && <Alert severity="error">{error}</Alert>}
            <DialogContent sx={{ p: 0 }}>
                {imageSrc ? (
                    <Box
                        sx={{
                            position: "relative",
                            height: "calc(100vh - 64px)",
                        }}
                    >
                        <Cropper
                            image={imageSrc}
                            crop={crop}
                            zoom={zoom}
                            aspect={1}
                            cropShape="round"
                            showGrid={false}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={(_, area) =>
                                setCroppedAreaPixels(area)
                            }
                        />
                    </Box>
                ) : (
                    <Stack
                        alignItems="center"
                        justifyContent="center"
                        sx={{ height: "60vh" }}
                    >
                        <CircularProgress />
                    </Stack>
                )}
            </DialogContent>
        </Dialog>
    );
};

export const NinioCard = ({
    ninio,
    onMarkAttend,
    onUndoAttend,
    onInfo,
    onPickPhoto,
    busy,
    disabled,
    onRecover,
}) => {
    const [expanded, setExpanded] = useState(false);
    const [imageFailed, setImageFailed] = useState(false);

    useEffect(() => {
        if (!expanded) return;
        const timer = setTimeout(() => setExpanded(false), 10000);
        return () => clearTimeout(timer);
    }, [expanded]);

    useEffect(() => setImageFailed(false), [ninio.signedUrl]);

    return (
        <Card
            onClick={() => {
                if (busy) return;

                if (onRecover) {
                    if (!disabled) onRecover(ninio);
                } else {
                    setExpanded((v) => !v);
                }
            }}
            sx={{
                position: "relative",
                width: "100%",
                minWidth: 0,
                aspectRatio: "1 / 1",
                overflow: "hidden",
                boxShadow: 3,
                cursor: "pointer",
                border: "3px solid",
                boxSizing: "border-box",
                borderColor:
                    onRecover || ninio.inscrito
                        ? "transparent"
                        : "warning.main",
            }}
        >
            {ninio.signedUrl && !imageFailed ? (
                <Box
                    component="img"
                    src={ninio.signedUrl}
                    alt={ninio.nombre}
                    loading="lazy"
                    onError={() => setImageFailed(true)}
                    sx={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        filter: expanded ? "brightness(0.85)" : "none",
                        transition: "filter 150ms ease",
                    }}
                />
            ) : (
                <Box
                    sx={{
                        position: "absolute",
                        inset: 0,
                        display: "grid",
                        placeItems: "center",
                        bgcolor: "background.default",
                    }}
                >
                    <Avatar sx={{ width: 96, height: 96, fontSize: 36 }}>
                        {(ninio.nombre || "N")[0]?.toUpperCase()}
                    </Avatar>
                </Box>
            )}

            <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                    position: "absolute",
                    right: 8,
                    top: 8,
                    display: "flex",
                    gap: 1,
                    zIndex: 2,
                }}
            >
                {!!ninio.alergias_medicinas?.trim() && (
                    <Tooltip
                        title={`Alergias / medicinas: ${ninio.alergias_medicinas}`}
                    >
                        <IconButton
                            size="small"
                            aria-label="alergias o medicinas"
                            onClick={() => (onRecover || onInfo)(ninio)}
                            sx={{
                                bgcolor: "error.main",
                                color: "common.white",
                                boxShadow: 2,
                                "&:hover": { bgcolor: "error.dark" },
                            }}
                        >
                            <LocalHospitalIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                )}

                {!ninio.signedUrl && (
                    <IconButton
                        size="small"
                        aria-label="Añadir foto"
                        disabled={busy}
                        onClick={() => (onRecover || onPickPhoto)(ninio)}
                        sx={{
                            bgcolor: "background.paper",
                            boxShadow: 2,
                            "&:hover": { bgcolor: "background.paper" },
                        }}
                    >
                        <PhotoCameraIcon fontSize="small" />
                    </IconButton>
                )}

                {!ninio.inscrito && (
                    <Tooltip title="Inscripción pendiente">
                        <Box
                            sx={{
                                bgcolor: "warning.main",
                                px: 0.6,
                                borderRadius: 1,
                                fontWeight: 800,
                                fontSize: 12,
                                alignSelf: "center",
                            }}
                        >
                            !
                        </Box>
                    </Tooltip>
                )}
            </Box>

            {expanded && (
                <IconButton
                    aria-label={"Ver información de " + ninio.nombre}
                    onClick={(e) => {
                        e.stopPropagation();
                        setExpanded(false);
                        onInfo(ninio);
                    }}
                    sx={{
                        position: "absolute",
                        top: "30%",
                        left: "50%",
                        width: 64,
                        height: 64,
                        transform: "translate(-50%, -50%)",
                        bgcolor: "rgba(255,255,255,.85)",
                        color: "text.primary",
                        "&:hover": { bgcolor: "white" },
                    }}
                >
                    <InfoOutlinedIcon />
                </IconButton>
            )}

            <Box
                sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    p: 1.25,
                    pt: 2.5,
                    background:
                        "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,.35) 35%, rgba(0,0,0,.55) 100%)",
                }}
            >
                <Typography
                    variant="subtitle1"
                    title={ninio.nombre}
                    sx={{
                        color: "common.white",
                        fontWeight: 600,
                        textShadow: "0 1px 2px rgba(0,0,0,.6)",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                    }}
                >
                    {ninio.nombre}
                </Typography>
            </Box>

            <Box
                sx={{
                    position: "absolute",
                    top: 8,
                    left: 8,
                    px: 1,
                    py: 0.25,
                    borderRadius: 2,
                    bgcolor: ninio.attendedToday
                        ? "success.main"
                        : "warning.main",
                    color: "common.white",
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    textTransform: "uppercase",
                    boxShadow: 2,
                }}
            >
                {onRecover
                    ? "Añadir al curso"
                    : ninio.attendedToday
                      ? "Asistió"
                      : "Pendiente"}
            </Box>

            <Box
                onClick={(e) => e.stopPropagation()}
                sx={{
                    position: "absolute",
                    left: 8,
                    right: 8,
                    bottom: 8,
                    display: "grid",
                    gridTemplateColumns: "1fr",
                    gap: 0.75,
                    transform: expanded ? "translateY(0)" : "translateY(120%)",
                    transition: "transform 160ms ease",
                    pointerEvents: expanded ? "auto" : "none",
                }}
            >
                <Button
                    color={ninio.attendedToday ? "warning" : "success"}
                    variant="contained"
                    disabled={busy || disabled || !expanded}
                    sx={{ borderRadius: 2 }}
                    startIcon={
                        busy ? (
                            <CircularProgress size={16} color="inherit" />
                        ) : ninio.attendedToday ? (
                            <UndoIcon />
                        ) : (
                            <CheckCircleIcon />
                        )
                    }
                    onClick={async () => {
                        await (ninio.attendedToday
                            ? onUndoAttend(ninio)
                            : onMarkAttend(ninio));
                        setExpanded(false);
                    }}
                >
                    {busy
                        ? "Guardando…"
                        : ninio.attendedToday
                          ? "No asistió"
                          : "Asiste"}
                </Button>
            </Box>
        </Card>
    );
};

// The shared form keeps creation, editing and recovery consistent.
const NinioFormDialog = ({
    open,
    ninio,
    curso,
    dia,
    editing,
    options = [],
    onClose,
    onSaved,
}) => {
    const [nombre, setNombre] = useState("");
    const [telefono, setTelefono] = useState("");
    const [padres, setPadres] = useState("");
    const [alergiasMedicinas, setAlergiasMedicinas] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [rawFile, setRawFile] = useState(null);
    const [finalFile, setFinalFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [cropOpen, setCropOpen] = useState(false);

    const coincidencias = useMemo(
        () =>
            !ninio && nombre.trim()
                ? new Fuse(
                      options.map((n) => ({
                          ...n,
                          busqueda: [n.nombre, n.padres, n.telefono]
                              .filter(Boolean)
                              .join(" ")
                              .normalize("NFD")
                              .replace(/[\u0300-\u036f]/g, ""),
                      })),
                      {
                          keys: ["busqueda"],
                          threshold: 0.3,
                          ignoreLocation: true,
                      },
                  )
                      .search(
                          nombre
                              .trim()
                              .normalize("NFD")
                              .replace(/[\u0300-\u036f]/g, ""),
                      )
                      .slice(0, 4)
                      .map((r) => r.item)
                : [],
        [ninio, nombre, options],
    );

    const theme = useTheme();
    const isXs = useMediaQuery(theme.breakpoints.only("xs"));
    const [createStep, setCreateStep] = useState(0);
    const [createMsg, setCreateMsg] = useState("");
    const fileInputRef = useRef(null);
    const fileInputCamRef = useRef(null);
    const savedId = useRef(null);
    const photoPath = useRef(null);
    const saving = useRef(false);

    useEffect(() => {
        if (!open) return;
        setNombre(ninio?.nombre || "");
        setTelefono(ninio?.telefono || "");
        setPadres(ninio?.padres || "");
        setAlergiasMedicinas(ninio?.alergias_medicinas || "");
        setError("");
        setRawFile(null);
        setFinalFile(null);
        setCropOpen(false);
        setCreateStep(0);
        setCreateMsg("");

        savedId.current = ninio?.id || null;
        photoPath.current = null;
    }, [open, ninio]);

    useEffect(() => {
        if (!finalFile) {
            setPreviewUrl(null);
            return;
        }
        const url = URL.createObjectURL(finalFile);
        setPreviewUrl(url);
        photoPath.current = null;
        return () => URL.revokeObjectURL(url);
    }, [finalFile]);

    const onFileChange = (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (f) {
            setRawFile(f);
            setCropOpen(true);
        }
    };

    const handleSave = async (mark) => {
        if (
            saving.current ||
            !nombre.trim() ||
            !curso?.id ||
            (mark && dia > todayDateOnly())
        )
            return;

        saving.current = true;
        setBusy(true);
        setError("");
        setCreateStep(1);
        setCreateMsg(editing || ninio ? "Guardando ficha…" : "Creando ficha…");

        try {
            const row = {
                nombre: nombre.trim(),
                telefono: telefono.trim(),
                padres: padres.trim(),
                alergias_medicinas: alergiasMedicinas.trim(),
            };

            const { data, error: saveError } = await (
                savedId.current
                    ? supabase
                          .from("CJENinios")
                          .update(row)
                          .eq("id", savedId.current)
                    : supabase
                          .from("CJENinios")
                          .insert({ ...row, origen: "manual" })
            )
                .select("id")
                .single();

            if (saveError) throw saveError;
            savedId.current = data.id;
            onSaved(false);

            const { error: courseError } = await supabase
                .from("CJEInscripciones")
                .upsert(
                    { ninio_id: data.id, curso_id: curso.id },
                    {
                        onConflict: "ninio_id,curso_id",
                        ignoreDuplicates: true,
                    },
                );

            if (courseError) throw courseError;

            if (finalFile) {
                setCreateStep(2);
                setCreateMsg("Subiendo foto…");
                photoPath.current ||=
                    "Ninios_" + data.id + "_" + crypto.randomUUID() + ".jpg";

                const { error: uploadError } = await supabase.storage
                    .from("NiniosFotos")
                    .upload(photoPath.current, finalFile, {
                        upsert: true,
                        contentType: "image/jpeg",
                    });

                if (uploadError) {
                    throw new Error(
                        "La ficha está guardada. Puedes reintentar la foto sin crear otro niño.",
                    );
                }

                setCreateStep(3);
                setCreateMsg("Actualizando ficha…");

                const { error: photoError } = await supabase
                    .from("CJENinios")
                    .update({ foto_path: photoPath.current })
                    .eq("id", data.id)
                    .select("id")
                    .single();

                if (photoError) throw photoError;
            }

            if (mark) {
                const { error: attendanceError } = await supabase
                    .from("CJEAsistenciaNinios")
                    .upsert(
                        { id: data.id, dia },
                        {
                            onConflict: "id,dia",
                            ignoreDuplicates: true,
                        },
                    );

                if (attendanceError) throw attendanceError;
            }

            setCreateStep(4);
            setCreateMsg("¡Listo!");
            onSaved(true);
            onClose();
        } catch (e) {
            setError(e.message || "No se pudo guardar. Puedes reintentarlo.");
        } finally {
            saving.current = false;
            setBusy(false);
        }
    };

    const steps = finalFile
        ? ["Crear ficha", "Subir foto", "Actualizar ficha"]
        : ["Crear ficha", "Actualizar ficha"];

    const activeIndex =
        createStep === 0
            ? -1
            : createStep === 4
              ? steps.length
              : createStep === 1
                ? 0
                : finalFile && createStep === 2
                  ? 1
                  : steps.length - 1;

    return (
        <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth>
            <DialogTitle>
                {editing
                    ? "Editar niño"
                    : ninio
                      ? "Añadir al curso"
                      : "Crear niño"}{" "}
                · {curso?.id}
            </DialogTitle>
            <DialogContent>
                {!editing && (busy || error) && (
                    <Stack spacing={1} sx={{ mb: 2 }}>
                        <Stepper activeStep={activeIndex} alternativeLabel>
                            {steps.map((label) => (
                                <Step key={label}>
                                    <StepLabel>{label}</StepLabel>
                                </Step>
                            ))}
                        </Stepper>
                        {busy && <LinearProgress />}
                        {createMsg && !error && (
                            <Typography variant="body2" color="text.secondary">
                                {createMsg}
                            </Typography>
                        )}
                    </Stack>
                )}

                <Stack spacing={2} sx={{ mt: 1 }}>
                    {!editing && (
                        <Alert severity="info">
                            Puedes confirmar la inscripción desde la información
                            del niño
                        </Alert>
                    )}
                    {error && <Alert severity="error">{error}</Alert>}
                    {!!coincidencias.length && (
                        <Alert severity="warning">
                            Hay fichas parecidas:{" "}
                            {coincidencias.map((n) => n.nombre).join(", ")}. Si
                            ya existe, cierra este diálogo y usa «Recuperar de
                            otro curso».
                        </Alert>
                    )}

                    <TextField
                        label="Nombre"
                        value={nombre}
                        autoFocus
                        disabled={busy}
                        onChange={(e) => setNombre(e.target.value)}
                    />
                    <TextField
                        label={editing ? "Teléfono (opcional)" : "Teléfono"}
                        type="tel"
                        inputProps={{ inputMode: "tel" }}
                        value={telefono}
                        disabled={busy}
                        onChange={(e) => setTelefono(e.target.value)}
                    />
                    <TextField
                        label="Alergias / medicinas (opcional)"
                        value={alergiasMedicinas}
                        multiline
                        minRows={2}
                        disabled={busy}
                        onChange={(e) => setAlergiasMedicinas(e.target.value)}
                        helperText="Indica «Sin alergias conocidas» si está confirmado. Vacío significa sin información."
                    />
                    <TextField
                        label="Padres / tutores"
                        value={padres}
                        disabled={busy}
                        multiline
                        onChange={(e) => setPadres(e.target.value)}
                    />

                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{ flexWrap: "wrap" }}
                    >
                        {isXs ? (
                            <>
                                <Button
                                    startIcon={<PhotoCameraIcon />}
                                    variant="outlined"
                                    disabled={busy}
                                    onClick={() =>
                                        fileInputRef.current?.click()
                                    }
                                >
                                    Galería
                                </Button>
                                <Button
                                    startIcon={<PhotoCameraIcon />}
                                    variant="contained"
                                    disabled={busy}
                                    onClick={() =>
                                        fileInputCamRef.current?.click()
                                    }
                                >
                                    Cámara
                                </Button>
                            </>
                        ) : (
                            <Button
                                startIcon={<PhotoCameraIcon />}
                                variant="outlined"
                                disabled={busy}
                                onClick={() => fileInputRef.current?.click()}
                            >
                                {editing || ninio
                                    ? "Cambiar foto"
                                    : "Añadir foto (opcional)"}
                            </Button>
                        )}

                        {previewUrl || ninio?.signedUrl ? (
                            <Avatar
                                src={previewUrl || ninio.signedUrl}
                                alt={nombre}
                                sx={{ width: 44, height: 44, ml: 1 }}
                            />
                        ) : editing ? (
                            <Avatar sx={{ width: 44, height: 44, ml: 1 }}>
                                {(nombre || "N")[0]?.toUpperCase()}
                            </Avatar>
                        ) : (
                            <Chip label="Sin foto" size="small" />
                        )}
                    </Stack>

                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        hidden
                        onChange={onFileChange}
                        disabled={busy}
                    />
                    <input
                        ref={fileInputCamRef}
                        type="file"
                        accept="image/*"
                        capture="environment"
                        hidden
                        onChange={onFileChange}
                        disabled={busy}
                    />
                </Stack>
            </DialogContent>

            <DialogActions sx={{ flexWrap: "wrap" }}>
                <Button onClick={onClose} disabled={busy}>
                    Cancelar
                </Button>
                <Button
                    onClick={() => handleSave(false)}
                    variant="contained"
                    disabled={busy || !nombre.trim() || !curso?.id}
                >
                    {busy
                        ? editing || ninio
                            ? "Guardando..."
                            : "Creando..."
                        : editing || ninio
                          ? "Guardar"
                          : "Crear"}
                </Button>
                {!editing && (
                    <Button
                        variant="outlined"
                        onClick={() => handleSave(true)}
                        disabled={
                            busy ||
                            !nombre.trim() ||
                            !curso?.id ||
                            dia > todayDateOnly()
                        }
                    >
                        Guardar y marcar · {dia}
                    </Button>
                )}
            </DialogActions>

            <PhotoCropDialog
                open={cropOpen}
                rawFile={rawFile}
                onClose={() => setCropOpen(false)}
                onConfirm={(f) => {
                    setFinalFile(f);
                    setCropOpen(false);
                }}
            />
        </Dialog>
    );
};

export const CreateNinioDialog = (props) => (
    <NinioFormDialog {...props} editing={false} />
);

export const EditNinioDialog = (props) => (
    <NinioFormDialog {...props} editing />
);

export const NinioInfoDialog = ({
    ninio,
    cursos,
    version,
    onClose,
    onEdit,
    onLink,
    onDay,
    onAddCourse,
    onSaved,
}) => {
    const [historial, setHistorial] = useState(null);
    const [error, setError] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setHistorial(null);
        setError("");

        if (ninio)
            (async () => {
                try {
                    const [cs, days] = await Promise.all([
                        readAll(
                            supabase
                                .from("CJEInscripciones")
                                .select("curso_id,inscrito")
                                .eq("ninio_id", ninio.id)
                                .order("curso_id", { ascending: false }),
                        ),
                        readAll(
                            supabase
                                .from("CJEAsistenciaNinios")
                                .select("dia")
                                .eq("id", ninio.id)
                                .order("dia", { ascending: false }),
                        ),
                    ]);

                    if (!cancelled) {
                        setHistorial({
                            cursos: cs,
                            asistencias: days.map((d) => d.dia),
                        });
                    }
                } catch (e) {
                    if (!cancelled)
                        setError(e.message || "No se pudo cargar el historial");
                }
            })();

        return () => {
            cancelled = true;
        };
    }, [ninio?.id, version]);

    return (
        <Dialog
            open={!!ninio}
            onClose={busy ? undefined : onClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>{ninio?.nombre}</DialogTitle>
            <DialogContent>
                {ninio && (
                    <Stack spacing={2}>
                        <Stack direction="row" spacing={2} alignItems="center">
                            <Avatar
                                src={ninio.signedUrl}
                                sx={{ width: 72, height: 72 }}
                            />
                            <Box>
                                <Typography>Curso {ninio.curso_id}</Typography>
                                <Chip
                                    size="small"
                                    label={
                                        ninio.inscrito
                                            ? "Inscripción confirmada"
                                            : "Inscripción pendiente"
                                    }
                                    color={
                                        ninio.inscrito ? "success" : "warning"
                                    }
                                />
                            </Box>
                        </Stack>

                        <Typography variant="body2" color="text.secondary">
                            Origen:{" "}
                            {ninio.origen === "formulario"
                                ? "Formulario"
                                : ninio.origen === "manual"
                                  ? "Manual"
                                  : "Datos anteriores"}
                        </Typography>

                        <FormControlLabel
                            label="Inscripción confirmada para este curso"
                            control={
                                <Checkbox
                                    checked={!!ninio.inscrito}
                                    disabled={busy}
                                    onChange={async (e) => {
                                        setBusy(true);
                                        setError("");

                                        try {
                                            const { error: err } =
                                                await supabase
                                                    .from("CJEInscripciones")
                                                    .update({
                                                        inscrito:
                                                            e.target.checked,
                                                    })
                                                    .eq("ninio_id", ninio.id)
                                                    .eq(
                                                        "curso_id",
                                                        ninio.curso_id,
                                                    )
                                                    .select("ninio_id")
                                                    .single();

                                            if (err) throw err;
                                            onSaved(true);
                                        } catch (e) {
                                            setError(
                                                e.message ||
                                                    "No se pudo guardar",
                                            );
                                        } finally {
                                            setBusy(false);
                                        }
                                    }}
                                />
                            }
                        />

                        <Typography sx={{ whiteSpace: "pre-wrap" }}>
                            <strong>Padres / tutores: </strong>
                            {ninio.padres || "Sin información"}
                        </Typography>

                        <Typography>
                            <strong>Teléfono: </strong>
                            {ninio.telefono || "Sin información"}
                        </Typography>

                        {(ninio.telefono || "")
                            .split(" / ")
                            .filter(Boolean)
                            .map((telefono) => (
                                <Button
                                    key={telefono}
                                    component="a"
                                    href={
                                        "tel:" + telefono.replace(/[^\d+]/g, "")
                                    }
                                    variant="outlined"
                                >
                                    Llamar · {telefono}
                                </Button>
                            ))}

                        <Typography sx={{ whiteSpace: "pre-wrap" }}>
                            <strong>Alergias / medicación: </strong>
                            {ninio.alergias_medicinas || "Sin información"}
                        </Typography>

                        <Divider />

                        {error ? (
                            <Alert severity="error">{error}</Alert>
                        ) : !historial ? (
                            <CircularProgress size={24} />
                        ) : (
                            <>
                                <Typography fontWeight={700}>
                                    Cursos e historial ·{" "}
                                    {historial.asistencias.length} asistencias
                                </Typography>

                                {historial.cursos.map((c) => {
                                    const curso = cursos.find(
                                        (v) => v.id === c.curso_id,
                                    );
                                    const asistencias = historial.asistencias
                                        .filter(
                                            (d) =>
                                                curso &&
                                                d >= curso.inicio &&
                                                (!curso.fin || d <= curso.fin),
                                        )
                                        .sort();
                                    return (
                                        <Box key={c.curso_id}>
                                            <Typography fontWeight={600}>
                                                {c.curso_id} ·{" "}
                                                {asistencias.length} asistencias
                                            </Typography>
                                            <Typography variant="body2">
                                                {c.inscrito
                                                    ? "Inscripción confirmada"
                                                    : "Inscripción pendiente"}
                                            </Typography>
                                            <Button
                                                size="small"
                                                color="error"
                                                disabled={busy}
                                                onClick={async () => {
                                                    if (
                                                        !window.confirm(
                                                            `¿Quitar a ${ninio.nombre} del curso ${c.curso_id}? Su ficha y asistencias se conservarán.`,
                                                        )
                                                    )
                                                        return;

                                                    setBusy(true);
                                                    setError("");

                                                    try {
                                                        const { error: err } =
                                                            await supabase
                                                                .from(
                                                                    "CJEInscripciones",
                                                                )
                                                                .delete()
                                                                .eq(
                                                                    "ninio_id",
                                                                    ninio.id,
                                                                )
                                                                .eq(
                                                                    "curso_id",
                                                                    c.curso_id,
                                                                )
                                                                .select(
                                                                    "ninio_id",
                                                                )
                                                                .single();

                                                        if (err) throw err;
                                                        onSaved(true);
                                                        onClose();
                                                    } catch (e) {
                                                        setError(
                                                            e.message ||
                                                                "No se pudo quitar del curso",
                                                        );
                                                    } finally {
                                                        setBusy(false);
                                                    }
                                                }}
                                            >
                                                Quitar del curso
                                            </Button>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    flexWrap: "nowrap",
                                                    gap: 0.5,
                                                    overflowX: "auto",
                                                    pb: 0.5,
                                                    "& > *": { flexShrink: 0 },
                                                }}
                                            >
                                                {asistencias.map((d) => (
                                                    <Chip
                                                        key={d}
                                                        size="small"
                                                        label={parseYMD(
                                                            d,
                                                        ).toLocaleDateString(
                                                            "es-ES",
                                                        )}
                                                        disabled={busy}
                                                        onClick={() => onDay(d)}
                                                        sx={{
                                                            bgcolor: [
                                                                "#eeeeee",
                                                                "#dce6ef",
                                                                "#b8d3eb",
                                                                "#86b8e2",
                                                                "#5199d5",
                                                                "#1976d2",
                                                                "#3889d6",
                                                                "#65a3df",
                                                                "#97c0e9",
                                                                "#c2dcf2",
                                                                "#e3eff9",
                                                                "#f5f9fd",
                                                            ][
                                                                parseYMD(
                                                                    d,
                                                                ).getMonth()
                                                            ],
                                                            color: [
                                                                4, 5, 6,
                                                            ].includes(
                                                                parseYMD(
                                                                    d,
                                                                ).getMonth(),
                                                            )
                                                                ? "#fff"
                                                                : "#263238",
                                                            border: "1px solid",
                                                            borderColor:
                                                                "rgba(0, 0, 0, 0.12)",
                                                        }}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    );
                                })}

                                {cursos.some(
                                    (c) =>
                                        !historial.cursos.some(
                                            (i) => i.curso_id === c.id,
                                        ),
                                ) && (
                                    <TextField
                                        select
                                        label="Añadir a otro curso"
                                        value=""
                                        disabled={busy}
                                        onChange={(e) =>
                                            onAddCourse(ninio, e.target.value)
                                        }
                                    >
                                        {cursos
                                            .filter(
                                                (c) =>
                                                    !historial.cursos.some(
                                                        (i) =>
                                                            i.curso_id === c.id,
                                                    ),
                                            )
                                            .map((c) => (
                                                <MenuItem
                                                    key={c.id}
                                                    value={c.id}
                                                >
                                                    {c.id}
                                                </MenuItem>
                                            ))}
                                    </TextField>
                                )}
                            </>
                        )}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions sx={{ flexWrap: "wrap" }}>
                <Button disabled={busy} onClick={() => onLink(ninio)}>
                    Unir duplicado
                </Button>
                <Button disabled={busy} onClick={() => onEdit(ninio)}>
                    Editar
                </Button>
                <Button disabled={busy} onClick={onClose}>
                    Cerrar
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export const NinioPickerDialog = ({
    open,
    ninio,
    options = [],
    curso,
    onClose,
    onRecover,
    onLinked,
}) => {
    const [search, setSearch] = useState("");
    const [selected, setSelected] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [usarOrigen, setUsarOrigen] = useState(false);

    const fuse = useMemo(
        () =>
            new Fuse(
                options.map((n) => ({
                    ...n,
                    busqueda: [n.nombre, n.padres, n.telefono]
                        .filter(Boolean)
                        .join(" ")
                        .normalize("NFD")
                        .replace(/[\u0300-\u036f]/g, ""),
                })),
                { keys: ["busqueda"], threshold: 0.35, ignoreLocation: true },
            ),
        [options],
    );

    const results = search.trim()
        ? fuse
              .search(
                  search
                      .trim()
                      .normalize("NFD")
                      .replace(/[\u0300-\u036f]/g, ""),
              )
              .map((r) => r.item)
        : options;

    useEffect(() => {
        setSearch(ninio?.nombre || "");
        setSelected(null);
        setError("");
        setUsarOrigen(false);
    }, [open, ninio]);

    const confirm = async () => {
        if (busy || !selected) return;
        if (!ninio) {
            onRecover(selected);
            return;
        }

        setBusy(true);
        setError("");

        try {
            const { error: err } = await supabase.rpc("cje_vincular", {
                p_origen: ninio.id,
                p_destino: selected.id,
                p_usar_origen: usarOrigen,
            });

            if (err) throw err;
            onLinked();
            onClose();
        } catch (e) {
            setError(e.message || "No se pudieron unir las fichas");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={busy ? undefined : onClose}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle>
                {ninio ? "Unir " + ninio.nombre : "Recuperar de otro curso"}
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}

                    <TextField
                        label="Buscar niño"
                        value={search}
                        disabled={busy}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setSelected(null);
                        }}
                    />

                    <Box sx={{ maxHeight: 220, overflow: "auto" }}>
                        {results.map((n) => (
                            <Button
                                key={n.id}
                                fullWidth
                                sx={{ justifyContent: "flex-start" }}
                                disabled={busy}
                                variant={
                                    selected?.id === n.id ? "outlined" : "text"
                                }
                                onClick={() => setSelected(n)}
                            >
                                {n.nombre} · #{n.id}
                            </Button>
                        ))}
                        {!results.length && (
                            <Typography>
                                No hay coincidencias. Prueba con otro nombre.
                            </Typography>
                        )}
                    </Box>

                    {selected && (
                        <>
                            <Typography fontWeight={700}>
                                {selected.nombre} · #{selected.id}
                            </Typography>
                            <Typography sx={{ whiteSpace: "pre-wrap" }}>
                                Padres/tutores:{" "}
                                {selected.padres || "Sin información"}
                                {"\n"}Teléfono:{" "}
                                {selected.telefono || "Sin información"}
                                {"\n"}Alergias/medicación:{" "}
                                {selected.alergias_medicinas ||
                                    "Sin información"}
                            </Typography>

                            {ninio && (
                                <>
                                    <Alert severity="warning">
                                        Se conservará la ficha #{selected.id} y
                                        se borrará la #{ninio.id}. Se unirán sus
                                        cursos, inscripciones, asistencias y
                                        datos de contacto.
                                    </Alert>
                                    <TextField
                                        select
                                        label="Nombre y foto que quieres conservar"
                                        disabled={busy}
                                        value={
                                            usarOrigen ? "origen" : "destino"
                                        }
                                        onChange={(e) =>
                                            setUsarOrigen(
                                                e.target.value === "origen",
                                            )
                                        }
                                    >
                                        <MenuItem value="destino">
                                            {selected.nombre} · #{selected.id}
                                        </MenuItem>
                                        <MenuItem value="origen">
                                            {ninio.nombre} · #{ninio.id}
                                        </MenuItem>
                                    </TextField>
                                    <Typography variant="body2">
                                        Datos de la otra ficha:{" "}
                                        {ninio.padres || "Sin tutores"}
                                        {" · "}
                                        {ninio.telefono || "Sin teléfono"}
                                        {" · "}
                                        {ninio.alergias_medicinas ||
                                            "Sin información médica"}
                                    </Typography>
                                </>
                            )}
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={busy}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    onClick={confirm}
                    disabled={!selected || busy}
                >
                    {busy
                        ? "Guardando…"
                        : ninio
                          ? "Confirmar unión"
                          : "Añadir a " + curso?.id}
                </Button>
            </DialogActions>
        </Dialog>
    );
};
